import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

type CurrentSale = {
  id: number;
  first_name: string;
  last_name: string;
  avatar?: { src?: string };
  administrator: boolean;
  disabled: boolean;
  pending: boolean;
  workspace_id: string;
};

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();
      if (sale == null) throw new Error("No se encontró el acceso del usuario");
      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
      };
    },
  });

const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getSessionStorage(): Storage | null {
  if (typeof window !== "undefined" && window.sessionStorage) {
    return window.sessionStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getSessionStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) return cachedValue === "true";

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = (data?.at(0)?.is_initialized ?? 0) > 0;
  if (isInitialized) storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  return isInitialized;
}

const getSale = async (
  forceRefresh = false,
): Promise<CurrentSale | undefined> => {
  const storage = getSessionStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (!forceRefresh && cachedValue != null)
    return JSON.parse(cachedValue) as CurrentSale;

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();
  if (dataSession?.session?.user == null || errorSession) return undefined;

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, first_name, last_name, avatar, administrator, disabled, pending, workspace_id",
    )
    .eq("user_id", dataSession.session.user.id)
    .single();

  if (dataSale == null || errorSale) return undefined;
  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale as CurrentSale;
};

function clearCache() {
  const storage = getSessionStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

const isPublicAuthRoute = () =>
  ["/set-password", "/forgot-password", "/sign-up", "/access-pending"].some(
    (path) =>
      window.location.pathname === path ||
      window.location.hash.includes(`#${path}`),
  );

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.provider === "google") {
        const { error } = await getSupabaseClient().auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/`,
            queryParams: { prompt: "select_account" },
          },
        });
        if (error) throw error;
        return;
      }
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) throw error;
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      if (isPublicAuthRoute()) return;
      await baseAuthProvider.checkAuth(params);

      const sale = await getSale(true);
      if (sale == null || sale.pending || sale.disabled) {
        throw {
          redirectTo: "/access-pending",
          message: false,
        };
      }
    },
    canAccess: async (params) => {
      const sale = await getSale();
      if (sale == null || sale.pending || sale.disabled) return false;
      return canAccess(sale.administrator ? "admin" : "user", params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
